import { useCallback, useRef } from 'react';

import { MOVE_ANNOTATIONS, UPDATE_STATE } from '../actions';
import { buildMoveUpdateFromNode } from '../actions/moveAnnotations';
import { UPDATE_ZINDEX_ANNOTATIONS } from '../actions/updateZIndexAnnotations';
import { getIdsToMoveWithPrimary } from '../utils/annotationGroupUtils';
import {
  buildCardBackgroundPatchForText,
  hasTemplateCardBackground,
} from '../utils/textTemplateCardLayout';
import { TOOLS_IDS } from '../utils/constants';
import {
  collectSnapTargetsFromNodes,
  computeDragSnapAdjustment,
  SNAP_THRESHOLD,
} from '../utils/computeDragSnap';
import {
  clearSnapGuidesOverlay,
  drawSnapGuidesOverlay,
  serializeSnapGuides,
} from '../utils/snapGuidesOverlay';
import useStore from './useStore';

const useAnnotationDragHandlers = () => {
  const {
    annotations,
    designLayer,
    dispatch,
    selectionsIds = [],
    editorPreferences = {},
  } = useStore();
  const snapToGuides = Boolean(editorPreferences.snapToGuides);
  const dragSessionRef = useRef(null);

  const detachDragSession = useCallback(() => {
    const session = dragSessionRef.current;
    dragSessionRef.current = null;

    if (session?.guidesRafId) {
      cancelAnimationFrame(session.guidesRafId);
    }

    clearSnapGuidesOverlay(designLayer);
  }, [designLayer]);

  const syncGroupFollowers = useCallback(
    (session, dx, dy) => {
      session.memberIds.forEach((memberId) => {
        if (memberId === session.primaryId) {
          return;
        }
        if (annotations[memberId]?.locked) {
          return;
        }
        const start = session.startPositions[memberId];
        const node = designLayer?.findOne(`#${memberId}`);
        if (!start || !node) {
          return;
        }
        node.position({
          x: start.x + dx,
          y: start.y + dy,
        });
      });
    },
    [annotations, designLayer],
  );

  const scheduleSnapGuidesDraw = useCallback(
    (session, guides) => {
      const guidesKey = serializeSnapGuides(guides);
      if (session.lastGuidesKey === guidesKey) {
        return;
      }

      if (session.guidesRafId) {
        cancelAnimationFrame(session.guidesRafId);
      }

      session.guidesRafId = requestAnimationFrame(() => {
        session.guidesRafId = null;
        if (dragSessionRef.current !== session) {
          return;
        }
        session.lastGuidesKey = drawSnapGuidesOverlay(designLayer, guides);
      });
    },
    [designLayer],
  );

  const updateSnapGuidesForDrag = useCallback(
    (session, dx, dy) => {
      const proposedRect = {
        x: session.startClientRect.x + dx,
        y: session.startClientRect.y + dy,
        width: session.startClientRect.width,
        height: session.startClientRect.height,
      };

      const excludeIds = new Set(session.memberIds);
      const targets = collectSnapTargetsFromNodes(designLayer, excludeIds);
      const { guides } = computeDragSnapAdjustment(
        proposedRect,
        targets,
        SNAP_THRESHOLD,
      );

      scheduleSnapGuidesDraw(session, guides);
    },
    [designLayer, scheduleSnapGuidesDraw],
  );

  const handleDragStart = useCallback(
    (e) => {
      const primaryId = e.target.id();
      const primary = annotations[primaryId];
      const primaryNode = e.target;

      if (!primary || primary.locked) {
        e.target.stopDrag();
        dragSessionRef.current = null;
        return;
      }

      const memberIds = getIdsToMoveWithPrimary(
        annotations,
        primaryId,
        selectionsIds,
      );
      const startPositions = {};

      memberIds.forEach((memberId) => {
        const node = designLayer?.findOne(`#${memberId}`);
        if (node) {
          startPositions[memberId] = {
            x: node.x(),
            y: node.y(),
          };
        }
      });

      const startClientRect = primaryNode.getClientRect({
        relativeTo: designLayer,
      });

      dragSessionRef.current = {
        primaryId,
        memberIds,
        startPositions,
        startPrimary: startPositions[primaryId],
        startClientRect,
        lastGuidesKey: null,
        guidesRafId: null,
      };

      // Bring to front imperatively — avoid Redux during drag (prevents react-konva reset flicker).
      primaryNode.moveToTop();
      designLayer?.batchDraw();
    },
    [annotations, designLayer, selectionsIds],
  );

  const handleDragMove = useCallback(
    (e) => {
      const session = dragSessionRef.current;
      if (!session || e.target.id() !== session.primaryId) {
        return;
      }

      const primaryNode = e.target;
      const dx = primaryNode.x() - session.startPrimary.x;
      const dy = primaryNode.y() - session.startPrimary.y;

      if (session.memberIds.length > 1) {
        syncGroupFollowers(session, dx, dy);
      }

      if (snapToGuides) {
        updateSnapGuidesForDrag(session, dx, dy);
      } else {
        clearSnapGuidesOverlay(designLayer);
      }
    },
    [designLayer, snapToGuides, syncGroupFollowers, updateSnapGuidesForDrag],
  );

  const handleDragEnd = useCallback(() => {
    const session = dragSessionRef.current;
    detachDragSession();

    if (!session) {
      return;
    }

    const primaryNode = designLayer?.findOne(`#${session.primaryId}`);
    if (!primaryNode || !session.startPrimary) {
      return;
    }

    let dx = primaryNode.x() - session.startPrimary.x;
    let dy = primaryNode.y() - session.startPrimary.y;

    if (snapToGuides) {
      const proposedRect = {
        x: session.startClientRect.x + dx,
        y: session.startClientRect.y + dy,
        width: session.startClientRect.width,
        height: session.startClientRect.height,
      };
      const excludeIds = new Set(session.memberIds);
      const targets = collectSnapTargetsFromNodes(designLayer, excludeIds);
      const { dx: snapDx, dy: snapDy } = computeDragSnapAdjustment(
        proposedRect,
        targets,
        SNAP_THRESHOLD,
      );
      dx += snapDx;
      dy += snapDy;

      primaryNode.position({
        x: session.startPrimary.x + dx,
        y: session.startPrimary.y + dy,
      });
      if (session.memberIds.length > 1) {
        syncGroupFollowers(session, dx, dy);
      }
    }

    dispatch({
      type: UPDATE_ZINDEX_ANNOTATIONS,
      payload: { id: session.primaryId },
    });

    const updates = session.memberIds
      .map((memberId) => {
        const node = designLayer?.findOne(`#${memberId}`);
        return buildMoveUpdateFromNode(annotations[memberId], node);
      })
      .filter(Boolean);

    if (updates.length === 0) {
      return;
    }

    const primaryAnnotation = annotations[session.primaryId];
    const needsCardSync =
      primaryAnnotation?.name === TOOLS_IDS.TEXT &&
      primaryNode &&
      hasTemplateCardBackground(annotations, session.primaryId);

    if (needsCardSync) {
      dispatch({
        type: UPDATE_STATE,
        payload: (state) => {
          const annotationPatches = {};
          updates.forEach(({ id, ...patch }) => {
            annotationPatches[id] = { ...state.annotations[id], ...patch };
          });
          const primaryNode = designLayer?.findOne(`#${session.primaryId}`);
          const mergedText = {
            ...annotationPatches[session.primaryId],
            x: primaryNode.x(),
            y: primaryNode.y(),
            width: primaryNode.width() * primaryNode.scaleX(),
            height: primaryNode.height() * primaryNode.scaleY(),
            rotation: primaryNode.rotation(),
          };
          annotationPatches[session.primaryId] = mergedText;
          const cardPatch = buildCardBackgroundPatchForText(
            { ...state.annotations, ...annotationPatches },
            mergedText,
          );
          if (cardPatch) {
            annotationPatches[cardPatch.id] = {
              ...state.annotations[cardPatch.id],
              ...cardPatch,
            };
          }
          return { isDesignState: true, annotations: annotationPatches };
        },
      });
    } else {
      dispatch({
        type: MOVE_ANNOTATIONS,
        payload: { updates },
      });
    }
  }, [
    annotations,
    designLayer,
    detachDragSession,
    dispatch,
    snapToGuides,
    syncGroupFollowers,
  ]);

  return {
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
  };
};

export default useAnnotationDragHandlers;
